import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocaleSwitcher } from "@/components/layout/locale-switcher";
import { useAuth } from "@/auth/auth-provider";
import { authErrorMessage } from "@/auth/errors";

const schema = z
  .object({
    email: z
      .string()
      .min(1, "auth.signIn.errors.emailRequired")
      .email("auth.signIn.errors.emailInvalid"),
    code: z
      .string()
      .min(1, "auth.reset.codeRequired")
      .regex(/^\d+$/, "auth.reset.codeInvalid"),
    newPassword: z
      .string()
      .min(8, "auth.newPassword.errors.minLength")
      .regex(/[A-Z]/, "auth.newPassword.errors.uppercase")
      .regex(/[a-z]/, "auth.newPassword.errors.lowercase")
      .regex(/[0-9]/, "auth.newPassword.errors.number")
      .regex(/[^A-Za-z0-9]/, "auth.newPassword.errors.symbol"),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "auth.newPassword.errors.mismatch",
    path: ["confirmPassword"],
  });
type Values = z.infer<typeof schema>;

const searchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { confirmPasswordReset, requestPasswordReset } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: search.email ?? "",
      code: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await confirmPasswordReset(values.email, values.code, values.newPassword);
      toast.success(t("auth.reset.successToast"));
      navigate({ to: "/login" });
    } catch (err) {
      setServerError(authErrorMessage(err));
    }
  });

  const resendCode = async () => {
    const email = form.getValues("email");
    const parsed = z.string().email().safeParse(email);
    if (!parsed.success) {
      form.setError("email", { message: "auth.reset.resendInvalidEmail" });
      return;
    }
    try {
      await requestPasswordReset(email);
      toast.success(t("auth.reset.resendSuccess"));
    } catch (err) {
      setServerError(authErrorMessage(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-6">
      <div className="absolute top-4 right-4">
        <LocaleSwitcher align="end" />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <img src="/logo.svg" alt="MStar" className="h-8 w-auto mb-2 text-foreground" />
          <CardTitle className="text-xl">{t("auth.reset.title")}</CardTitle>
          <CardDescription>{t("auth.reset.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.reset.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...form.register("email")}
                aria-invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email ? (
                <p className="text-xs text-destructive">
                  {t(form.formState.errors.email.message ?? "")}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="code">{t("auth.reset.code")}</Label>
                <button
                  type="button"
                  onClick={resendCode}
                  className="text-xs text-primary hover:underline"
                >
                  {t("auth.reset.resend")}
                </button>
              </div>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                {...form.register("code")}
                aria-invalid={!!form.formState.errors.code}
              />
              {form.formState.errors.code ? (
                <p className="text-xs text-destructive">
                  {t(form.formState.errors.code.message ?? "")}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {t("auth.reset.newPassword")}
              </Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...form.register("newPassword")}
                aria-invalid={!!form.formState.errors.newPassword}
              />
              {form.formState.errors.newPassword ? (
                <p className="text-xs text-destructive">
                  {t(form.formState.errors.newPassword.message ?? "")}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t("auth.newPassword.passwordHint")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t("auth.reset.confirmPassword")}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...form.register("confirmPassword")}
                aria-invalid={!!form.formState.errors.confirmPassword}
              />
              {form.formState.errors.confirmPassword ? (
                <p className="text-xs text-destructive">
                  {t(form.formState.errors.confirmPassword.message ?? "")}
                </p>
              ) : null}
            </div>

            {serverError ? (
              <div
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {serverError}
              </div>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t("auth.reset.submitting")}
                </>
              ) : (
                t("auth.reset.submit")
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                {t("auth.reset.backToSignIn")}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
